/**
 * The places a transfer booking names, each with its own label.
 *
 * A transfer has no meeting point. The pick-up stored on the booking is the
 * guest's accommodation, and on an airport arrival the driver meets the guest
 * at the airport and drives them TO it. Printing "Meeting point: <hotel>" sent
 * a guest to wait at his hotel (owner, 2026-09-24). So every surface labels
 * the accommodation as "Your accommodation" and the second place by its kind.
 */
export type TransferPlaceLineKind = "accommodation" | "restaurant" | "club" | "camp";

export interface TransferPlaceLine {
  kind: TransferPlaceLineKind;
  /** The name, plus the address when there is one that adds something. */
  value: string;
}

interface PlaceLike {
  name?: string | null;
  address?: string | null;
  typed?: boolean | null;
}

interface TransferLike {
  pickup?: PlaceLike | null;
  place?: (PlaceLike & { kind?: string | null }) | null;
}

/**
 * One place as a single line. A typed place is shown exactly as the guest
 * typed it; a picked place gets its address after the name when the address
 * is not just the name again.
 */
export function formatTransferPlace(place: PlaceLike): string {
  const name = place.name?.trim() ?? "";
  if (place.typed === true) return name;
  const address = place.address?.trim() ?? "";
  if (!address || address === name) return name;
  // Google often starts the address with the place's own name.
  if (name && address.startsWith(name)) return address;
  return name ? `${name}, ${address}` : address;
}

/**
 * The labelled place lines for a transfer booking, accommodation first.
 * Empty when the record has no usable place.
 */
export function transferPlaceLines(transfer: TransferLike | null | undefined): TransferPlaceLine[] {
  if (!transfer) return [];
  const lines: TransferPlaceLine[] = [];
  if (transfer.pickup) {
    const value = formatTransferPlace(transfer.pickup);
    if (value) lines.push({ kind: "accommodation", value });
  }
  const kind = transfer.place?.kind;
  if (transfer.place && (kind === "restaurant" || kind === "club" || kind === "camp")) {
    const value = formatTransferPlace(transfer.place);
    if (value) lines.push({ kind, value });
  }
  return lines;
}

/** True for a booking of a transfer route, by its record or its template. */
export function isTransferBooking(args: {
  transfer?: unknown;
  template?: string | null;
  categorySlug?: string | null;
}): boolean {
  return args.transfer != null || args.template === "transfer" || args.categorySlug === "transport";
}
